import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { FaCheckSquare } from "react-icons/fa";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";
import styled from "styled-components";
import { LanguageSwitcher } from "../shared";
import ATLTinkerpreneurRegistrationPrompt from "../ATLTinckerpreneur/ATLTinkerpreneurRegistrationPrompt";
import {
  fetchCanSubmit,
  fetchEntry,
  buildSubmissionFormData,
  confirmSubmission,
} from "../../utils/tinkerpreneurSubmission";

/**
 * Reusable, presentation-light project-submission form for AI Tinkerpreneur.
 *
 * All submission LOGIC (validation, file upload, API calls, gating, eligibility,
 * read-only-after-confirm) lives here and is shared across years. Everything that
 * varies per year is passed in by the (bespoke) page wrapper: the `year`, the option
 * lists (projectTypes/SDGs/themes), and the FAQ link.
 */

const StyledCheckIcon = styled(FaCheckSquare)`
  color: var(--success);
  font-size: 16px;
`;

const FlexCenter = styled.div`
  display: flex;
  align-items: center;
`;

const DEFAULT_UN_SDGS = [
  "No Poverty",
  "Zero Hunger",
  "Good Health and Well-Being",
  "Quality Education",
  "Gender Equality",
  "Clean Water and Sanitization",
  "Affordable and Clean Energy",
  "Decent Work and Economic Growth",
  "Industry, Innovation and Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities and Communities",
  "Responsible Consumption and Production",
  "Climate Action",
  "Life Below Water",
  "Life on Land",
  "Peace, Justice and Strong Institutions",
  "Partnerships for the Goals",
];

const DEFAULT_PROJECT_TYPES = ["Individual", "Group"];

const DEFAULT_THEMES = [
  "AI in Healthcare",
  "AI in Education",
  "AI in Sustainability",
  "AI in Agriculture",
  "AI in Safety",
  "AI in Accessibility",
  "AI in Smart Homes",
  "AI in Environment",
];

const TinkerpreneurSubmissionForm = ({
  year,
  projectTypes = DEFAULT_PROJECT_TYPES,
  unSdgs = DEFAULT_UN_SDGS,
  themes = DEFAULT_THEMES,
  faqUrl = "/india-ai-impact-festival-faqs",
  evaluationParamsUrl = null,
  titleKey = "tinkerpreneur_submission_page_title",
  confirmationKey = "tinkerpreneur_submission_confirmation",
  maxSubmittedKey = "tinkerpreneur_submission_max_submitted",
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [canSubmitData, setCanSubmitData] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  // Bumped after an in-place registration so eligibility is re-read and the form
  // unlocks without sending the student away from the page.
  const [eligibilityReloadKey, setEligibilityReloadKey] = useState(0);
  const { t } = useTranslation();

  const resolvedTitle = t(titleKey);
  const resolvedConfirmationText = t(confirmationKey);
  const resolvedMaxSubmittedText = t(maxSubmittedKey);

  useEffect(() => {
    const loadCanSubmit = async () => {
      try {
        const result = await fetchCanSubmit();
        setCanSubmitData(result);
      } catch (error) {
        console.error("Error fetching can-submit:", error);
      }
    };

    const loadEntry = async () => {
      try {
        const entry = await fetchEntry();
        setData(entry);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching entry:", error);
        setLoading(false);
      }
    };

    loadCanSubmit();
    loadEntry();
  }, [isSubmitted, eligibilityReloadKey]);

  const createFileUploadNotification = (type) => {
    switch (type) {
      case "submit_success":
        NotificationManager.success(
          "Project submitted!",
          "Congratulations! You have successfully submit your project for evaluatation.",
          3000
        );
        break;

      case "save_success":
        NotificationManager.success(
          "Congratulations!",
          "Your entry has been submitted successfully.",
          6000
        );
        break;

      default:
        NotificationManager.error(
          "Please try again or come back later.",
          "Uh-oh, there was an error!",
          6000
        );
        break;
    }
  };

  const editable = data?.status !== "confirmed";

  const RegistrationSchema = Yup.object().shape({
    readGuideline: Yup.boolean()
      .oneOf([true], t("validation.read_guideline"))
      .required(t("validation.read_guideline")),

    projects: Yup.array().of(
      Yup.object().shape({
        isExisting: Yup.boolean(),
        theme: Yup.string().when("isExisting", {
          is: false,
          then: (schema) => schema.required(t("validation.theme")),
        }),
        sdg: Yup.string().when("isExisting", {
          is: false,
          then: (schema) =>
            schema
              .required(t("validation.sdg"))
              .test(
                "isNotPlaceholder",
                t("validation.sdg"),
                (value) => value !== ""
              ),
        }),
        ethicalConcern: Yup.string(),
        projectType: Yup.string(),
        members: Yup.array().when(["projectType", "isExisting"], {
          is: (projectType, isExisting) =>
            !isExisting && projectType === "Group",
          then: (schema) =>
            schema
              .of(Yup.string().required(t("validation.project_member")))
              .min(1, t("validation.at_least_one_member")),
        }),
        pdf: Yup.mixed()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema
                .test(
                  "pdf-required",
                  t("validation.project_pdf"),
                  function (value) {
                    const pathParts = this.path.match(/projects\[(\d+)\]/);
                    const index = pathParts ? parseInt(pathParts[1]) : 0;
                    return !!value || !!data.projects?.[index]?.pdf;
                  }
                )
                .test(
                  "pdf-size",
                  t("validation.file_too_large", { max: "5MB" }),
                  function (value) {
                    if (value && value.size) {
                      return value.size <= 5 * 1024 * 1024;
                    }
                    return true;
                  }
                )
                .test(
                  "pdf-format",
                  t("validation.invalid_file_type", { type: "PDF" }),
                  function (value) {
                    if (value && value.name) {
                      return value.name.toLowerCase().endsWith(".pdf");
                    }
                    return true;
                  }
                ),
          }),
        video: Yup.mixed()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema
                .test(
                  "video-or-url",
                  t("validation.either_project_video_or_url"),
                  function (value) {
                    const pathParts = this.path.match(/projects\[(\d+)\]/);
                    const index = pathParts ? parseInt(pathParts[1]) : 0;
                    const { videoURL } = this.parent;
                    return (
                      !!value || !!videoURL || !!data.projects?.[index]?.video
                    );
                  }
                )
                .test(
                  "video-size",
                  t("validation.file_too_large", { max: "25MB" }),
                  function (value) {
                    if (value && value.size) {
                      return value.size <= 25 * 1024 * 1024;
                    }
                    return true;
                  }
                )
                .test(
                  "video-format",
                  t("validation.invalid_file_type", {
                    type: "MP4, MOV, WMV, FLV, AVI",
                  }),
                  function (value) {
                    if (value && value.name) {
                      const allowedExtensions = [
                        ".mp4",
                        ".mov",
                        ".wmv",
                        ".flv",
                        ".avi",
                      ];
                      return allowedExtensions.some((ext) =>
                        value.name.toLowerCase().endsWith(ext)
                      );
                    }
                    return true;
                  }
                ),
          }),

        videoURL: Yup.string()
          .nullable()
          .when("isExisting", {
            is: false,
            then: (schema) =>
              schema.test(
                "video-or-url",
                t("validation.either_project_video_or_url"),
                function (value) {
                  const pathParts = this.path.match(/projects\[(\d+)\]/);
                  const index = pathParts ? parseInt(pathParts[1]) : 0;
                  const { video } = this.parent;

                  return (
                    !!value || !!video || !!data.projects?.[index]?.videoURL
                  );
                }
              ),
          }),

        hasDeployedInnovation: Yup.string().when("isExisting", {
          is: false,
          then: (schema) =>
            schema.required(t("validation.has_deployed_innovation")),
        }),

        journeyDocumentURL: Yup.string()
          .nullable()
          .when(["hasDeployedInnovation", "isExisting"], {
            is: (hasDeployedInnovation, isExisting) =>
              !isExisting && hasDeployedInnovation === "yes",
            then: (schema) =>
              schema.required(t("validation.journey_document_url")),
          }),
      })
    ),
  });

  const handleSubmit = async (values) => {
    const confirmed = window.confirm(t("submission_page_confirmation_text"));
    if (!confirmed) return;

    try {
      const formData = buildSubmissionFormData(values);
      await confirmSubmission(formData);
      createFileUploadNotification("save_success");
      setIsSubmitted(!isSubmitted);
    } catch (error) {
      createFileUploadNotification("error");
    }
  };

  return (
    <>
      <ATLTinkerpreneurRegistrationPrompt
        year={year}
        open={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
        onRegistered={() => {
          setShowRegistrationPrompt(false);
          setEligibilityReloadKey((key) => key + 1);
        }}
      />
      {loading || canSubmitData.submissionOpen ? (
        <div
          className="iaiif-registration-page tinkerpreneur-submission gray-light-bg"
          style={{ paddingTop: 0 }}
        >
          <div className="container d-flex justify-content-end pt-1">
            <LanguageSwitcher />
          </div>

          <div className="blog-container shadow">
            <img
              src={`/images/tinkerpreneur-${year}/form-header.png`}
              alt={`India AI Tinkerpreneur ${year} sponsors`}
              className="w-100"
            />
            <div className="container">
              <div className="row user-info-container">
                <div className="col-md-12 pt-5">
                  <h1 className="text-center">
                    {resolvedTitle.replace("{year}", year)}
                  </h1>
                  <h2 className="text-center">
                    {t("submission_page_subtitle")}
                  </h2>
                </div>
              </div>
              <div>
                {loading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <div class="spinner-grow" role="status">
                      <span class="sr-only">{t("loading_text")}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    {!canSubmitData.eligible ? (
                      <div className="alert alert-warning">
                        {t("tinkerpreneur_submission_not_eligible")}{" "}
                        <button
                          type="button"
                          onClick={() => setShowRegistrationPrompt(true)}
                          className="btn btn-link p-0 align-baseline border-0"
                          style={{
                            // Darker than the festival orange (#ff8f4e), which is
                            // unreadable as text on the amber alert background.
                            color: "#c2410c",
                            fontWeight: 700,
                            textDecoration: "underline",
                            verticalAlign: "baseline",
                          }}
                        >
                          {t("tinkerpreneur_submission_register_cta")}
                        </button>
                      </div>
                    ) : !canSubmitData.canSubmit ? (
                      <div className="alert alert-success">
                        {resolvedMaxSubmittedText}
                      </div>
                    ) : (
                      data.status === "confirmed" && (
                        <div className="alert alert-success">
                          {resolvedConfirmationText}
                        </div>
                      )
                    )}

                    <div className="p-2">
                      <p className="text-muted">
                        {t("submission_page_disclaimer_part_1")}{" "}
                        <a href={faqUrl} className="text-primary">
                          {t("submission_page_disclaimer_link_text_faq")}
                        </a>{" "}
                        {t("submission_page_disclaimer_part_2")}.
                      </p>
                    </div>

                    <Formik
                      enableReinitialize
                      initialValues={{
                        readGuideline: false,
                        // Initialize with one project by default
                        projects:
                          data.projects && data.projects.length > 0
                            ? data.projects.map((project) => ({
                                theme: project.theme || "",
                                ethicalConcern: project.ethicalConcern || "",
                                sdg: project.sdg || "",
                                members: project.members || [""],
                                projectType: project.projectType || "",
                                videoURL: project.videoURL || "",
                                video: project.video,
                                pdf: project.pdf,
                                hasDeployedInnovation:
                                  project.hasDeployedInnovation || "",
                                journeyDocumentURL:
                                  project.journeyDocumentURL || "",
                                isExisting: true,
                              }))
                            : [
                                {
                                  theme: "",
                                  ethicalConcern: "",
                                  sdg: "",
                                  members: [""],
                                  projectType: "",
                                  videoURL: "",
                                  video: null,
                                  pdf: null,
                                  hasDeployedInnovation: "",
                                  journeyDocumentURL: "",
                                  isExisting: false,
                                },
                              ],
                      }}
                      validationSchema={RegistrationSchema}
                      onSubmit={(values) => handleSubmit(values)}
                    >
                      {({
                        setFieldValue,
                        values,
                        handleSubmit,
                        isSubmitting,
                      }) => {
                        return (
                          <Form>
                            <div className="form-row flex-wrap">
                              <div className="col-12 col-md-6 px-3 mb-3">
                                <div className="form-group">
                                  <label htmlFor="studentName">
                                    {t("submission_page_label_name")}
                                  </label>
                                  <Field
                                    type="text"
                                    name="studentName"
                                    className="form-control bg-light m-0"
                                    id="studentName"
                                    placeholder={`${data.first_name} ${data.last_name}`}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Single project section */}
                            {values.projects?.length > 0 && (
                              <div className="project-section mb-4 p-3 border rounded">
                                {(() => {
                                  const index = 0;
                                  const project = values.projects[0];
                                  return (
                                    <>
                                      <div className="form-row">
                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label htmlFor={`sdg-${index}`}>
                                              {t("submission_page_label_sdg")}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            <Field
                                              as="select"
                                              name={`projects[${index}].sdg`}
                                              className="form-control m-0"
                                              id={`sdg-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                            >
                                              <option value="">
                                                {t(
                                                  "submission_page_sdg_option_blank"
                                                )}
                                              </option>
                                              {unSdgs.map((sdg, idx) => (
                                                <option
                                                  key={sdg}
                                                  value={`SDG ${
                                                    idx + 1
                                                  }: ${sdg}`}
                                                >
                                                  {`SDG ${idx + 1}: ${t(
                                                    "submission_page_sdg_options." +
                                                      idx
                                                  )}`}
                                                </option>
                                              ))}
                                            </Field>
                                            <ErrorMessage
                                              name={`projects[${index}].sdg`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>

                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`ethicalConcern-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_ethical_concern"
                                              )}
                                            </label>
                                            <Field
                                              type="text"
                                              name={`projects[${index}].ethicalConcern`}
                                              className="form-control m-0"
                                              id={`ethicalConcern-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                              placeholder={t(
                                                "submission_page_label_ethical_concern_placeholder"
                                              )}
                                            />
                                            <ErrorMessage
                                              name={`projects[${index}].ethicalConcern`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="form-row">
                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label htmlFor={`theme-${index}`}>
                                              {t(
                                                "tinkerpreneur_submission_label_theme"
                                              )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            <Field
                                              as="select"
                                              name={`projects[${index}].theme`}
                                              className="form-control m-0"
                                              id={`theme-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                            >
                                              <option value="" disabled hidden>
                                                {t(
                                                  "tinkerpreneur_submission_theme_option_blank"
                                                )}
                                              </option>
                                              {themes.map((theme) => (
                                                <option
                                                  key={theme}
                                                  value={theme}
                                                >
                                                  {theme}
                                                </option>
                                              ))}
                                            </Field>
                                            <ErrorMessage
                                              name={`projects[${index}].theme`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>

                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`projectType-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_project_type"
                                              )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            <Field
                                              as="select"
                                              name={`projects[${index}].projectType`}
                                              className="form-control m-0"
                                              id={`projectType-${index}`}
                                              disabled={
                                                !editable && project.isExisting
                                              }
                                            >
                                              <option value="">
                                                {t(
                                                  "submission_page_project_type_option_blank"
                                                )}
                                              </option>
                                              {projectTypes.map(
                                                (projectType, i) => (
                                                  <option
                                                    key={projectType}
                                                    value={projectType}
                                                  >
                                                    {t(
                                                      "submission_page_project_type_options." +
                                                        i
                                                    )}
                                                  </option>
                                                )
                                              )}
                                            </Field>
                                            <ErrorMessage
                                              name={`projects[${index}].projectType`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {project.projectType === "Group" && (
                                        <div className="form-row">
                                          <div className="col-md-6 px-3 mb-3">
                                            <div className="form-group">
                                              <label>
                                                {t(
                                                  "tinkerpreneur_submission_members_label"
                                                )}
                                                <small className="text-danger">
                                                  *
                                                </small>
                                              </label>
                                              <FieldArray
                                                name={`projects[${index}].members`}
                                              >
                                                {({
                                                  push: pushMember,
                                                  remove: removeMember,
                                                }) => (
                                                  <>
                                                    {project.members?.map(
                                                      (member, memberIndex) => (
                                                        <div
                                                          key={memberIndex}
                                                          className="d-flex align-items-center mb-2"
                                                        >
                                                          <Field
                                                            type="text"
                                                            name={`projects[${index}].members[${memberIndex}]`}
                                                            className="form-control m-0"
                                                            disabled={
                                                              !editable &&
                                                              project.isExisting
                                                            }
                                                            placeholder={t(
                                                              "tinkerpreneur_submission_member_placeholder"
                                                            )}
                                                          />
                                                          {memberIndex > 0 &&
                                                            !project.isExisting && (
                                                              <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm ml-2"
                                                                onClick={() =>
                                                                  removeMember(
                                                                    memberIndex
                                                                  )
                                                                }
                                                              >
                                                                ×
                                                              </button>
                                                            )}
                                                        </div>
                                                      )
                                                    )}
                                                    {project.members?.length <
                                                      3 && (
                                                      <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() =>
                                                          pushMember("")
                                                        }
                                                      >
                                                        {t(
                                                          "tinkerpreneur_submission_add_member"
                                                        )}
                                                      </button>
                                                    )}
                                                  </>
                                                )}
                                              </FieldArray>
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      <div className="form-row">
                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`projectPDF-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_submit_pdf_1"
                                              )}{" "}
                                              {t(
                                                "submission_page_label_submit_pdf_project"
                                              )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>
                                            {data.projects?.[index]?.pdf && (
                                              <FlexCenter>
                                                <StyledCheckIcon />{" "}
                                                <small className="text-success ml-1">
                                                  {t(
                                                    "submission_page_label_submitted_text"
                                                  )}
                                                </small>
                                              </FlexCenter>
                                            )}

                                            {!project.isExisting && (
                                              <>
                                                <input
                                                  id={`projectPDF-${index}`}
                                                  name={`projects[${index}].pdf`}
                                                  type="file"
                                                  onChange={(event) =>
                                                    setFieldValue(
                                                      `projects[${index}].pdf`,
                                                      event.currentTarget
                                                        .files[0]
                                                    )
                                                  }
                                                  className="form-control-file mt-2"
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                />
                                                <small class="form-text text-muted">
                                                  {t(
                                                    "submission_page_submit_pdf_requirement"
                                                  )}
                                                </small>
                                              </>
                                            )}

                                            <ErrorMessage
                                              name={`projects[${index}].pdf`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                        </div>

                                        <div className="col-md-6 px-3 mb-3">
                                          <div className="form-group">
                                            <label
                                              htmlFor={`projectVideo-${index}`}
                                            >
                                              {t(
                                                "submission_page_label_submit_video"
                                              )}
                                              <small className="text-danger">
                                                *
                                              </small>
                                            </label>

                                            {data.projects?.[index]?.video && (
                                              <FlexCenter>
                                                <StyledCheckIcon />{" "}
                                                <small className="text-success ml-1">
                                                  {t(
                                                    "submission_page_label_submitted_text"
                                                  )}
                                                </small>
                                              </FlexCenter>
                                            )}

                                            {!project.isExisting && (
                                              <>
                                                <input
                                                  id={`projectVideo-${index}`}
                                                  name={`projects[${index}].video`}
                                                  type="file"
                                                  onChange={(event) =>
                                                    setFieldValue(
                                                      `projects[${index}].video`,
                                                      event.currentTarget
                                                        .files[0]
                                                    )
                                                  }
                                                  className="form-control-file mt-2"
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                />
                                                <small class="form-text text-muted">
                                                  {t(
                                                    "submission_page_submit_video_requirement"
                                                  )}
                                                </small>
                                              </>
                                            )}

                                            <ErrorMessage
                                              name={`projects[${index}].video`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </div>
                                          {!project.isExisting && (
                                            <div className="form-group">
                                              <label
                                                htmlFor={`videoURL-${index}`}
                                              >
                                                {t(
                                                  "submission_page_label_submit_video_url"
                                                )}
                                              </label>
                                              <small className="form-text">
                                                {t(
                                                  "submission_page_submit_video_url_description"
                                                )}
                                              </small>
                                              <>
                                                <Field
                                                  type="text"
                                                  name={`projects[${index}].videoURL`}
                                                  className="form-control m-0 mb-2"
                                                  id={`videoURL-${index}`}
                                                  disabled={
                                                    !editable &&
                                                    project.isExisting
                                                  }
                                                  placeholder="E.g., https://www.youtube.com/watch?v=HGYYEUSm-0Q"
                                                />
                                              </>
                                              <ErrorMessage
                                                name={`projects[${index}].videoURL`}
                                                component="div"
                                                className="text-danger"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {!project.isExisting && (
                                        <div className="form-row">
                                          <div className="col-md-6 px-3 mb-3">
                                            <div className="form-group">
                                              <label>
                                                {t(
                                                  "submission_page_label_has_deployed_innovation"
                                                )}
                                                <small className="text-danger">
                                                  *
                                                </small>
                                              </label>
                                              <div>
                                                <div className="form-check form-check-inline">
                                                  <Field
                                                    type="radio"
                                                    name={`projects[${index}].hasDeployedInnovation`}
                                                    value="yes"
                                                    id={`hasDeployedInnovation-yes-${index}`}
                                                    className="form-check-input"
                                                    disabled={!editable}
                                                  />
                                                  <label
                                                    className="form-check-label"
                                                    htmlFor={`hasDeployedInnovation-yes-${index}`}
                                                  >
                                                    {t(
                                                      "submission_page_special_award_option_yes"
                                                    )}
                                                  </label>
                                                </div>
                                                <div className="form-check form-check-inline">
                                                  <Field
                                                    type="radio"
                                                    name={`projects[${index}].hasDeployedInnovation`}
                                                    value="no"
                                                    id={`hasDeployedInnovation-no-${index}`}
                                                    className="form-check-input"
                                                    disabled={!editable}
                                                  />
                                                  <label
                                                    className="form-check-label"
                                                    htmlFor={`hasDeployedInnovation-no-${index}`}
                                                  >
                                                    {t(
                                                      "submission_page_special_award_option_no"
                                                    )}
                                                  </label>
                                                </div>
                                              </div>
                                              <ErrorMessage
                                                name={`projects[${index}].hasDeployedInnovation`}
                                                component="div"
                                                className="text-danger"
                                              />
                                            </div>
                                          </div>

                                          {project.hasDeployedInnovation ===
                                            "yes" && (
                                            <div className="col-md-6 px-3 mb-3">
                                              <div className="form-group">
                                                <label
                                                  htmlFor={`journeyDocumentURL-${index}`}
                                                >
                                                  {t(
                                                    "submission_page_label_journey_document_url"
                                                  )}
                                                  <small className="text-danger">
                                                    *
                                                  </small>
                                                </label>
                                                <Field
                                                  type="text"
                                                  name={`projects[${index}].journeyDocumentURL`}
                                                  className="form-control m-0 mb-2"
                                                  id={`journeyDocumentURL-${index}`}
                                                  disabled={!editable}
                                                  placeholder="E.g., https://drive.google.com/file/d/1a2B3c4D5e/view"
                                                />
                                                <small className="form-text text-muted">
                                                  {t(
                                                    "submission_page_journey_document_url_description"
                                                  )}
                                                </small>
                                                <ErrorMessage
                                                  name={`projects[${index}].journeyDocumentURL`}
                                                  component="div"
                                                  className="text-danger"
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="form-row">
                                        <div className="col-12">
                                          <p
                                            className="mb-1"
                                            style={{ color: "#cc0000" }}
                                          >
                                            {t("submission_page_label_note")}:
                                          </p>
                                          <ul
                                            className="mb-0 pl-4"
                                            style={{ color: "#cc0000" }}
                                          >
                                            <li>
                                              {t(
                                                "submission_page_note_project"
                                              )}
                                            </li>
                                            <li>
                                              {t(
                                                "submission_page_note_one_project"
                                              )}
                                            </li>
                                          </ul>
                                        </div>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Terms and conditions */}
                            <div className="form-row">
                              <div class="col-12 px-3 mb-md-0">
                                <div className="form-group">
                                  <div className="form-check form-check-inline">
                                    <Field
                                      type="checkbox"
                                      disabled={
                                        !editable && !canSubmitData?.canSubmit
                                      }
                                      className="form-check-input"
                                      name="readGuideline"
                                      id="readGuideline"
                                    />
                                    <label
                                      htmlFor="readGuideline"
                                      className="form-check-label"
                                    >
                                      {t("submission_page_terms_check_text")}{" "}
                                      <a href={faqUrl} className="text-primary">
                                        <strong>
                                          {t(
                                            "submission_page_disclaimer_link_text_faq"
                                          )}
                                        </strong>
                                      </a>
                                      .
                                    </label>
                                  </div>
                                  <ErrorMessage
                                    name="readGuideline"
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </div>
                            </div>

                            {canSubmitData?.canSubmit &&
                              !values.projects[0]?.isExisting && (
                                <div className="mt-2">
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() => handleSubmit()}
                                      disabled={!values.readGuideline}
                                      className="btn btn-primary mr-3 mt-3 mt-md-0 "
                                    >
                                      {isSubmitting && (
                                        <span
                                          class="spinner-grow spinner-grow-sm mr-2"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>
                                      )}
                                      {isSubmitting
                                        ? t(
                                            "submission_page_submitting_btn_text"
                                          )
                                        : t("submission_page_submit_btn_text")}
                                    </button>
                                  </div>
                                  {evaluationParamsUrl && (
                                    <small className="text-muted font-italic pt-2 d-block">
                                      {t("submission_page_download_text")}{" "}
                                      <a
                                        className="text-black"
                                        href={evaluationParamsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <strong>
                                          {t(
                                            "submission_page_evaluation_params_text"
                                          )}
                                        </strong>
                                      </a>
                                      .
                                    </small>
                                  )}
                                </div>
                              )}
                          </Form>
                        );
                      }}
                    </Formik>
                  </div>
                )}
              </div>
            </div>
          </div>
          <NotificationContainer />
        </div>
      ) : (
        <div className="iaiif-registration-page gray-light-bg footer-sticky-bottom">
          <div className="container d-flex justify-content-end">
            <LanguageSwitcher />
          </div>
          <div className="blog-container shadow">
            <div className="container">
              <div className="row align-items-center user-info-container">
                <div className="col-md-12">
                  <h1 className="text-center">
                    {resolvedTitle.replace("{year}", year)}
                  </h1>
                </div>
              </div>
              <div className="row justify-content-center">
                <div className="col-12 col-md-9 col-xl-7">
                  <div className="alert alert-info mx-auto my-3 my-md-5">
                    {t("tinkerpreneur_submission_closed")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TinkerpreneurSubmissionForm;
